/**
 * Komal OS - Main Application Component
 * Routes and layout for PWA
 */

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import useAuthStore from './store/authStore';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import PunchPointListPage from './pages/PunchPointListPage';
import CreatePunchPointPage from './pages/CreatePunchPointPage';
import TaskIssueImportPage from './pages/TaskIssueImportPage';
import DailyEntryPage from './pages/DailyEntryPage';
import PerformanceReportPage from './pages/PerformanceReportPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import ProjectsListPage from './pages/ProjectsListPage';
import CreateProjectPage from './pages/CreateProjectPage';
import EditProjectPage from './pages/EditProjectPage';
import PeoplePage from './pages/PeoplePage';
import CategoryConfigPage from './pages/CategoryConfigPage';
import PermissionsPage from './pages/PermissionsPage';
import ProductionTrackingPage from './pages/ProductionTrackingPage';
import DelayListPage from './pages/DelayListPage';
import MyScheduleTasksPage from './pages/MyScheduleTasksPage';
import WeeklyReviewListPage from './pages/WeeklyReviewListPage';
import WeeklyReviewPage from './pages/WeeklyReviewPage';
import RaiseRevisionPage from './pages/RaiseRevisionPage';
import DelayDetailPage from './pages/DelayDetailPage';
import QualityChecklistsPage from './pages/QualityChecklistsPage';
import InspectionsPage from './pages/InspectionsPage';
import NCRListPage from './pages/NCRListPage';
import CreateNCRPage from './pages/CreateNCRPage';
import NCRDetailPage from './pages/NCRDetailPage';
import PaymentsPage from './pages/PaymentsPage';
import ExpenseDashboardPage from './pages/expenses/ExpenseDashboardPage';
import SiteExpensesPage from './pages/expenses/SiteExpensesPage';
import FactoryExpensesPage from './pages/expenses/FactoryExpensesPage';
import VendorPaymentsPage from './pages/expenses/VendorPaymentsPage';
import MaterialPurchasesPage from './pages/expenses/MaterialPurchasesPage';
import ExpenseCeoSummaryPage from './pages/expenses/ExpenseCeoSummaryPage';
import CashFlowDashboardPage from './pages/cashflow/CashFlowDashboardPage';
import ReceivablesPage from './pages/cashflow/ReceivablesPage';
import MaterialsPage from './pages/cashflow/MaterialsPage';
import SiteRequirementsPage from './pages/cashflow/SiteRequirementsPage';
import AccountsPage from './pages/cashflow/AccountsPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import AiAssistantPage from './pages/AiAssistantPage';
import DesignCalculatorsHubPage from './pages/design/DesignCalculatorsHubPage';
import PipePressureDropCalculatorPage from './pages/design/PipePressureDropCalculatorPage';
import DesignCalculationsListPage from './pages/design/DesignCalculationsListPage';
import DesignCalculationDetailPage from './pages/design/DesignCalculationDetailPage';
import HeatExchangerCalculatorPage from './pages/design/HeatExchangerCalculatorPage';
import TankSizingCalculatorPage from './pages/design/TankSizingCalculatorPage';
import JacketDesignCalculatorPage from './pages/design/JacketDesignCalculatorPage';
import TankDesignCalculatorPage from './pages/design/TankDesignCalculatorPage';
import ProjectDesignSummaryPage from './pages/design/ProjectDesignSummaryPage';
import PSFCalculatorPage from './pages/design/PSFCalculatorPage';
import ACFCalculatorPage from './pages/design/ACFCalculatorPage';
import SoftenerCalculatorPage from './pages/design/SoftenerCalculatorPage';
import DMPlantCalculatorPage from './pages/design/DMPlantCalculatorPage';
import PipeSizingCalculatorPage from './pages/design/PipeSizingCalculatorPage';
import MyKRAPage from './pages/kra/MyKRAPage';
import MyKRACycleDetailPage from './pages/kra/MyKRACycleDetailPage';
import KRALibraryPage from './pages/kra/KRALibraryPage';
import TeamReviewPage from './pages/kra/TeamReviewPage';
import TeamReviewDetailPage from './pages/kra/TeamReviewDetailPage';
import CrossInputsPage from './pages/kra/CrossInputsPage';
import QuarterlyDashboardPage from './pages/kra/QuarterlyDashboardPage';
import CardScanPage from './pages/crm/CardScanPage';
import ScanQueuePage from './pages/crm/ScanQueuePage';
import AccountsListPage from './pages/crm/AccountsListPage';
import AccountDetailPage from './pages/crm/AccountDetailPage';
import PipelinePage from './pages/crm/PipelinePage';
import CrmDashboardPage from './pages/crm/CrmDashboardPage';
import ImportContactsPage from './pages/crm/ImportContactsPage';
import MultiCardScanPage from './pages/crm/MultiCardScanPage';
import BulkEmailPage from './pages/crm/BulkEmailPage';
import VendorsListPage from './pages/vendors/VendorsListPage';
import VendorDetailPage from './pages/vendors/VendorDetailPage';
import VendorImportPage from './pages/vendors/VendorImportPage';
import VendorCardScanPage from './pages/vendors/VendorCardScanPage';
import PayrollDashboardPage from './pages/payroll/PayrollDashboardPage';
import PayrollEmployeesPage from './pages/payroll/PayrollEmployeesPage';
import PayrollRunsPage from './pages/payroll/PayrollRunsPage';
import PayrollRunDetailPage from './pages/payroll/PayrollRunDetailPage';
import SalarySheetImportPage from './pages/payroll/SalarySheetImportPage';
import PayrollEmailImportPage from './pages/payroll/PayrollEmailImportPage';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import LoadingSpinner from './components/LoadingSpinner';
import ToastContainer from './components/ToastContainer';

function App() {
  const { isAuthenticated, verifyToken } = useAuthStore();
  const [loading, setLoading] = React.useState(true);

  useEffect(() => {
    // Verify token on app load
    const checkAuth = async () => {
      if (localStorage.getItem('auth_token')) {
        await verifyToken();
      }
      setLoading(false);
    };

    checkAuth();

    // Register service worker for PWA
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/serviceWorker.js').catch(err => {
        console.log('Service Worker registration failed:', err);
      });
    }
  }, [verifyToken]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Router>
      <ToastContainer />
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={!isAuthenticated ? <LoginPage /> : <Navigate to="/dashboard" />}
        />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/punch-points"
          element={
            <ProtectedRoute>
              <PunchPointListPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/punch-points/create"
          element={
            <ProtectedRoute>
              <CreatePunchPointPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/punch-points/import"
          element={
            <ProtectedRoute>
              <TaskIssueImportPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/daily-entry"
          element={
            <ProtectedRoute>
              <DailyEntryPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/performance"
          element={
            <ProtectedRoute>
              <PerformanceReportPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projects"
          element={
            <ProtectedRoute>
              <ProjectsListPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projects/create"
          element={
            <ProtectedRoute>
              <CreateProjectPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projects/:id"
          element={
            <ProtectedRoute>
              <ProjectDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projects/:id/edit"
          element={
            <ProtectedRoute>
              <EditProjectPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/people"
          element={
            <ProtectedRoute>
              <PeoplePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/categories"
          element={
            <ProtectedRoute>
              <CategoryConfigPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/permissions"
          element={
            <ProtectedRoute>
              <PermissionsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/production"
          element={
            <ProtectedRoute>
              <ProductionTrackingPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/delays"
          element={
            <ProtectedRoute>
              <DelayListPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/schedule/my-tasks"
          element={
            <ProtectedRoute>
              <MyScheduleTasksPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projects/:id/revision"
          element={
            <ProtectedRoute>
              <RaiseRevisionPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/schedule/reviews"
          element={
            <ProtectedRoute>
              <WeeklyReviewListPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/schedule/reviews/:projectId"
          element={
            <ProtectedRoute>
              <WeeklyReviewPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/delays/:id"
          element={
            <ProtectedRoute>
              <DelayDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/quality/checklists"
          element={
            <ProtectedRoute>
              <QualityChecklistsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/quality/inspections"
          element={
            <ProtectedRoute>
              <InspectionsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ncr"
          element={
            <ProtectedRoute>
              <NCRListPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ncr/create"
          element={
            <ProtectedRoute>
              <CreateNCRPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ncr/:id"
          element={
            <ProtectedRoute>
              <NCRDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/payments"
          element={
            <ProtectedRoute>
              <PaymentsPage />
            </ProtectedRoute>
          }
        />

        {/* Expense Management module (Komal_OS_Expense_Module_PRD.docx) -
            role gating is UI convenience only, real enforcement is
            server-side in app/routes/expense_routes.py. */}
        <Route
          path="/expenses"
          element={
            <ProtectedRoute>
              <ExpenseDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/expenses/site"
          element={
            <ProtectedRoute>
              <SiteExpensesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/expenses/factory"
          element={
            <ProtectedRoute>
              <FactoryExpensesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/expenses/vendor"
          element={
            <ProtectedRoute>
              <VendorPaymentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/expenses/purchases"
          element={
            <ProtectedRoute>
              <MaterialPurchasesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/expenses/summary"
          element={
            <ProtectedRoute>
              <ExpenseCeoSummaryPage />
            </ProtectedRoute>
          }
        />

        {/* Cash Flow Prediction module (built from a design conversation, see
            027_cash_flow_module.sql) - role gating is UI convenience only,
            real enforcement is server-side in app/routes/cashflow_routes.py. */}
        <Route
          path="/cashflow"
          element={
            <ProtectedRoute>
              <CashFlowDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cashflow/receivables"
          element={
            <ProtectedRoute>
              <ReceivablesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cashflow/materials"
          element={
            <ProtectedRoute>
              <MaterialsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cashflow/site"
          element={
            <ProtectedRoute>
              <SiteRequirementsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cashflow/accounts"
          element={
            <ProtectedRoute>
              <AccountsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/change-password"
          element={
            <ProtectedRoute>
              <ChangePasswordPage />
            </ProtectedRoute>
          }
        />

        {/* Ask Komal (AI assistant) - rolled out to HOD/CEO/HR only for now
            (HR included because an HR department head is functionally a
            HOD for that department). allowedRoles here is UI convenience
            only; real enforcement is server-side via require_ai_access in
            app/routes/ai_routes.py - same pattern as the Design
            Calculators restriction below. */}
        <Route
          path="/ai-assistant"
          element={
            <ProtectedRoute allowedRoles={['HOD', 'CEO', 'HR']}>
              <AiAssistantPage />
            </ProtectedRoute>
          }
        />

        {/* Design Calculators - restricted to HOD/CEO (narrowed 2026-08-15
            from the original Engineer/HOD/CEO/System_Admin set). allowedRoles
            here is UI convenience only; real enforcement is server-side via
            require_design_access in app/routes/design_routes.py - same
            pattern as the KRA module. */}
        <Route
          path="/design"
          element={
            <ProtectedRoute allowedRoles={['HOD', 'CEO']}>
              <DesignCalculatorsHubPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/design/pipe-pressure-drop"
          element={
            <ProtectedRoute allowedRoles={['HOD', 'CEO']}>
              <PipePressureDropCalculatorPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/design/heat-exchanger"
          element={
            <ProtectedRoute allowedRoles={['HOD', 'CEO']}>
              <HeatExchangerCalculatorPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/design/tank-sizing"
          element={
            <ProtectedRoute allowedRoles={['HOD', 'CEO']}>
              <TankSizingCalculatorPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/design/jacket-design"
          element={
            <ProtectedRoute allowedRoles={['HOD', 'CEO']}>
              <JacketDesignCalculatorPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/design/tank-design"
          element={
            <ProtectedRoute allowedRoles={['HOD', 'CEO']}>
              <TankDesignCalculatorPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/design/calculations"
          element={
            <ProtectedRoute allowedRoles={['HOD', 'CEO']}>
              <DesignCalculationsListPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/design/calculations/:id"
          element={
            <ProtectedRoute allowedRoles={['HOD', 'CEO']}>
              <DesignCalculationDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/design/project-summary"
          element={
            <ProtectedRoute allowedRoles={['HOD', 'CEO']}>
              <ProjectDesignSummaryPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/design/psf"
          element={
            <ProtectedRoute allowedRoles={['HOD', 'CEO']}>
              <PSFCalculatorPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/design/acf"
          element={
            <ProtectedRoute allowedRoles={['HOD', 'CEO']}>
              <ACFCalculatorPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/design/softener"
          element={
            <ProtectedRoute allowedRoles={['HOD', 'CEO']}>
              <SoftenerCalculatorPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/design/dm-plant"
          element={
            <ProtectedRoute allowedRoles={['HOD', 'CEO']}>
              <DMPlantCalculatorPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/design/pipe-sizing"
          element={
            <ProtectedRoute allowedRoles={['HOD', 'CEO']}>
              <PipeSizingCalculatorPage />
            </ProtectedRoute>
          }
        />

        {/* KRA module - allowedRoles is UI convenience only; real
            enforcement is server-side in app/auth_dependency.py */}
        <Route
          path="/kra"
          element={
            <ProtectedRoute>
              <MyKRAPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/kra/:id"
          element={
            <ProtectedRoute>
              <MyKRACycleDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/kra/library"
          element={
            <ProtectedRoute allowedRoles={['HR', 'CEO']}>
              <KRALibraryPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/kra/team"
          element={
            <ProtectedRoute allowedRoles={['HOD', 'HR', 'CEO']}>
              <TeamReviewPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/kra/team/:cycleId"
          element={
            <ProtectedRoute allowedRoles={['HOD', 'HR', 'CEO']}>
              <TeamReviewDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/kra/cross-inputs"
          element={
            <ProtectedRoute>
              <CrossInputsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/kra/quarterly"
          element={
            <ProtectedRoute allowedRoles={['CEO', 'HR']}>
              <QuarterlyDashboardPage />
            </ProtectedRoute>
          }
        />

        {/* CRM + Card Scanner module - server-side access is scoped to the
            Sales Department + CEO (app/routes/crm_routes.py's
            require_crm_access, checked by department name not role, since
            role encodes position level - HOD/Engineer/etc - not
            department). No allowedRoles here: ProtectedRoute only supports
            role-based gating and this app's role field can't express
            "which department" - the sidebar link is hidden the same way
            (see Sidebar.js), and the real enforcement is server-side. */}
        <Route
          path="/crm/scan"
          element={
            <ProtectedRoute>
              <CardScanPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/queue"
          element={
            <ProtectedRoute>
              <ScanQueuePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/accounts"
          element={
            <ProtectedRoute>
              <AccountsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/accounts/:accountId"
          element={
            <ProtectedRoute>
              <AccountDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/pipeline"
          element={
            <ProtectedRoute>
              <PipelinePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/dashboard"
          element={
            <ProtectedRoute>
              <CrmDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/accounts/import"
          element={
            <ProtectedRoute>
              <ImportContactsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/bulk-email"
          element={
            <ProtectedRoute>
              <BulkEmailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/scan/multi"
          element={
            <ProtectedRoute>
              <MultiCardScanPage />
            </ProtectedRoute>
          }
        />

        {/* Vendor Directory - own top-level section, deliberately not
            nested under CRM (different audience, different purpose - see
            app/routes/vendor_routes.py's module docstring). Server-side
            access is CEO + Design/Procurement/Project/Production/
            Operations HODs; no allowedRoles here for the same reason as
            the CRM routes above - role alone can't express "which
            department", so the sidebar link is hidden the same way (see
            Sidebar.js) and the real enforcement is server-side. */}
        <Route
          path="/vendors"
          element={
            <ProtectedRoute>
              <VendorsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/vendors/import"
          element={
            <ProtectedRoute>
              <VendorImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/vendors/scan"
          element={
            <ProtectedRoute>
              <VendorCardScanPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/vendors/:vendorId"
          element={
            <ProtectedRoute>
              <VendorDetailPage />
            </ProtectedRoute>
          }
        />

        {/* HR Payroll - HR/CEO/System_Admin (matches app/routes/payroll_routes.py's
            require_role("HR", "CEO", "System_Admin") on every endpoint; salary/
            bank/PF data gets the same confidentiality posture as the KRA
            module). Sidebar shows a single "HR Payroll" link (Dashboard/
            Employee Master/Payroll Runs are reached via the in-page
            PayrollTabs bar, not separate sidebar entries). */}
        <Route
          path="/payroll/dashboard"
          element={
            <ProtectedRoute allowedRoles={['HR', 'CEO', 'System_Admin']}>
              <PayrollDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/employees"
          element={
            <ProtectedRoute allowedRoles={['HR', 'CEO', 'System_Admin']}>
              <PayrollEmployeesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/runs"
          element={
            <ProtectedRoute allowedRoles={['HR', 'CEO', 'System_Admin']}>
              <PayrollRunsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/runs/:id"
          element={
            <ProtectedRoute allowedRoles={['HR', 'CEO', 'System_Admin']}>
              <PayrollRunDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/import"
          element={
            <ProtectedRoute allowedRoles={['HR', 'CEO', 'System_Admin']}>
              <SalarySheetImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/import-emails"
          element={
            <ProtectedRoute allowedRoles={['HR', 'CEO', 'System_Admin']}>
              <PayrollEmailImportPage />
            </ProtectedRoute>
          }
        />

        {/* Default redirect */}
        <Route
          path="/"
          element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} />}
        />
      </Routes>
    </Router>
  );
}

export default App;
